import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PublicThemeControl from "@/components/PublicThemeControl";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import BrandLockup from "@/components/BrandLockup";

export default function LoginPage() {
  const { user, login, register } = useAuth();
  const [searchParams] = useSearchParams();
  const invitedEmail = (searchParams.get("email") || "").trim().toLowerCase();
  const invitationMode = searchParams.get("mode") === "register" || Boolean(invitedEmail);
  const [mode, setMode] = useState(invitationMode ? "register" : "login");
  const [email, setEmail] = useState(invitedEmail);
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [professional, setProfessional] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [resending, setResending] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back");
        nav("/dashboard");
      } else {
        const result = await register(email, password, name, { privacy, terms, professional });
        const targetEmail = result?.email || email;
        toast.success(result?.message || "Check your email to verify your Callsect account.");
        nav(`/verify-email?email=${encodeURIComponent(targetEmail)}`);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === "email_verification_required") {
        setVerificationEmail(detail.email || email);
      }

      const invitationRequired =
        error?.response?.status === 403 &&
        (detail?.code === "beta_invitation_required" ||
          detail === "This private beta is invitation-only. Use the email address included in your invitation.");

      if (invitationRequired) {
        const cleanEmail = email.trim().toLowerCase();
        toast.error(
          typeof detail === "object"
            ? detail.message
            : "This private beta is invitation-only. Request access from the Callsect landing page if you have not received an invitation.",
          {
            duration: 12000,
            action: {
              label: "Request beta access",
              onClick: () =>
                nav(
                  `/?request=beta${
                    cleanEmail ? `&email=${encodeURIComponent(cleanEmail)}` : ""
                  }`
                ),
            },
          }
        );
      } else {
        toast.error(
          typeof detail === "string"
            ? detail
            : detail?.message || "Authentication failed"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    const targetEmail = verificationEmail || email;
    if (!targetEmail) return;
    setResending(true);
    try {
      const { data } = await api.post("/auth/resend-verification", { email: targetEmail });
      toast.success(data?.message || "Verification email sent.");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : detail?.message || "Could not send verification email."
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="login-page">
      <aside className="login-visual" aria-label="Callsect product overview">
        <img
          src="https://images.unsplash.com/photo-1710438399422-2fca27686bcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjBtaW5pbWFsaXN0JTIwdGV4dHVyZXxlbnwwfHx8fDE3ODMzMzc0MTN8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="login-visual__image"
        />
        <div className="login-visual__overlay" />
        <div className="login-visual__content">
          <div className="login-brand">
            <BrandLockup className="brand-lockup--login-hero" variant="dark" />
          </div>

          <div className="login-visual__message">
            <div className="login-eyebrow">
              <span className="login-eyebrow__lines" aria-hidden="true"><i /><i /><i /></span>
              Call dissection
            </div>
            <h1>
              <span>Your sales call. Fully decoded.</span>
              <small>Not just another AI summary.</small>
            </h1>
            <p>
              Every call tells a story. Callsect reveals the one you almost missed — hidden objections,
              buying signals, customer psychology, next-step coaching, and CRM notes written in your own voice.
            </p>
          </div>

          <footer className="login-visual__footer">
            <span>© 2026 Callsect</span>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Beta Terms</Link>
            <Link to="/data-handling">Data handling</Link>
          </footer>
        </div>
      </aside>

      <main className="login-panel">
        <div className="login-panel__toolbar">
          <PublicThemeControl />
        </div>

        <section className="login-form-shell">
          <header className="login-form-header">
            <div className="login-form-header__mobile-brand">
              <BrandLockup className="brand-lockup--login-mobile" />
            </div>
            <div className="login-form-kicker">{mode === "login" ? "Welcome back" : "Get started"}</div>
            <h2>{mode === "login" ? "Sign in" : "Create your account"}</h2>
            {mode === "register" && (
              <p>Private beta access is invitation-only. Register with the email address included in your invitation.</p>
            )}
          </header>

          <form onSubmit={submit} className="login-form">
            {mode === "register" && (
              <label className="login-field">
                <span>Name</span>
                <input
                  data-testid="name-input"
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                  placeholder="Jane Doe"
                  autoComplete="name"
                />
              </label>
            )}

            <label className="login-field">
              <span>Email</span>
              <input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                placeholder="jane@company.com"
                autoComplete="email"
              />
            </label>

            <label className="login-field">
              <span>Password</span>
              <input
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                minLength={10}
                maxLength={72}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                placeholder="••••••••"
              />
              {mode === "register" && <small>At least 10 characters; maximum 72 bytes.</small>}
              {mode === "login" && (
                <Link to="/forgot-password" className="login-forgot-link">Forgot your password?</Link>
              )}
            </label>

            {mode === "login" && verificationEmail && (
              <div className="login-verification" role="status">
                <p>Your email address still needs to be verified.</p>
                <button type="button" onClick={resendVerification} disabled={resending}>
                  {resending ? "Sending…" : "Resend verification email"}
                </button>
              </div>
            )}

            {mode === "register" && (
              <div className="login-consents">
                <ConsentCheck checked={privacy} onChange={setPrivacy}>
                  I have read and acknowledge the <Link to="/privacy" target="_blank" rel="noreferrer">Privacy Notice</Link>.
                </ConsentCheck>
                <ConsentCheck checked={terms} onChange={setTerms}>
                  I accept the <Link to="/terms" target="_blank" rel="noreferrer">Private Beta Terms</Link>.
                </ConsentCheck>
                <ConsentCheck checked={professional} onChange={setProfessional}>
                  I am at least 18 and will use Callsect for professional evaluation.
                </ConsentCheck>
              </div>
            )}

            <button
              data-testid="submit-auth-btn"
              type="submit"
              disabled={loading || (mode === "register" && (!privacy || !terms || !professional))}
              className="login-primary-button"
            >
              {loading ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
            </button>

            <button
              type="button"
              data-testid="toggle-auth-mode-btn"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setVerificationEmail("");
              }}
              className="login-mode-toggle"
            >
              {mode === "login"
                ? "New here? Create an account →"
                : "Already have an account? Sign in →"}
            </button>
          </form>

          <footer className="login-mobile-footer">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Beta Terms</Link>
            <Link to="/data-handling">Data handling</Link>
          </footer>
        </section>
      </main>
    </div>
  );
}

function ConsentCheck({ checked, onChange, children }) {
  return (
    <label className={`login-consent${checked ? " is-checked" : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>{children}</span>
    </label>
  );
}
